/**
 * Financials tab on the project detail page (spec §5.3, §6.1, §8).
 *
 * Consolidates the financial side of a project:
 *
 *   1. Project totals — contract value, supplier cost, margin
 *   2. Parts breakdown — per-part contract value, cost, margin
 *   3. Sub-parts breakdown — per-sub-part required qty + cost
 *   4. Earnest money summary
 *   5. Bank Guarantee amount summary
 *
 * This is a *reporting* surface. Workflow controls (BG instrument
 * management, billing-stage date entry) live on the Billing tab.
 */
import { Banknote, TrendingDown, TrendingUp, Wallet } from 'lucide-react';
import { formatBpsAsPercent, formatCurrency, formatDate } from '@procurement/shared';
import { Alert } from '@/components/ui/alert';
import { Card } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorPanel } from '@/components/ui/error-panel';
import { Spinner } from '@/components/ui/spinner';
import { StatTile } from '@/components/ui/stat-tile';
import { StatusBadge } from '@/components/ui/status-badge';
import {
  buildPartFinancialRows,
  buildSubPartFinancialRows,
  computeScheduleAmountComparison,
  type PartFinancialRow,
  type SubPartFinancialRow,
} from '@/lib/project-financials';
import {
  useBankGuarantees,
  useProjectItems,
  type BankGuarantee,
} from '@/lib/pipeline-queries';
import type { Project } from '@/lib/project-queries';

interface Props {
  project: Project;
}

export function ProjectFinancialsTab({ project }: Props) {
  const items = useProjectItems(project.id);
  const bgs = useBankGuarantees(project.id);

  if (items.isLoading || bgs.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="h-6 w-6 text-secondary" />
      </div>
    );
  }
  // Patch 5 — explicit error state
  if (items.isError || bgs.isError) {
    return (
      <ErrorPanel
        title="Could not load financials"
        onRetry={() => {
          void items.refetch();
          void bgs.refetch();
        }}
      />
    );
  }

  const allItems = items.data ?? [];
  const partRows = buildPartFinancialRows(allItems);
  const subPartRows = buildSubPartFinancialRows(allItems);
  const activeBgs = (bgs.data ?? []).filter((bg) => bg.deletedAt === null);

  return (
    <div className="space-y-6">
      <DeclaredContractValueCard project={project} />
      <ProjectTotalsBlock project={project} />
      <PartsBreakdownCard rows={partRows} />
      {subPartRows.length > 0 ? <SubPartsBreakdownCard rows={subPartRows} /> : null}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <EarnestMoneyCard project={project} />
        <BgAmountSummaryCard bgs={activeBgs} />
      </div>
    </div>
  );
}


function DeclaredContractValueCard({ project }: { project: Project }) {
  const comparison = computeScheduleAmountComparison(
    project.totalStoreValuePaisa,
    project.contractValuePaisa,
  );
  return (
    <Card title="Declared contract value">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
        <MiniValue label="Original amount" value={formatOptionalCurrency(project.totalStoreValuePaisa)} />
        <MiniValue label="GST %" value={formatBpsAsPercent(project.gstRateBps)} />
        <MiniValue label="GST amount" value={formatOptionalCurrency(project.gstAmountPaisa)} />
        <MiniValue
          label="Total contract value"
          value={formatOptionalCurrency(project.declaredTotalContractValuePaisa)}
        />
        <MiniValue
          label="SOS total"
          value={project.contractValuePaisa > 0 ? formatCurrency(project.contractValuePaisa) : '—'}
        />
        <MiniValue
          label="Discrepancy"
          value={formatScheduleAmountDiscrepancy(comparison)}
        />
      </div>
      {comparison.hasDiscrepancy ? (
        <Alert tone="warning" title="Amount discrepancy" className="mt-3">
          Original project amount and Schedule of Stores total differ by{' '}
          <span className="font-semibold tabular-nums">
            {formatCurrency(comparison.absoluteDiscrepancyPaisa ?? 0)}
          </span>
          .
        </Alert>
      ) : null}
      <p className="mt-2 text-xs text-secondary">
        Original amount comes from General Particulars. SOS total is computed from
        entered parts and may differ until the Schedule of Stores baseline is finalised.
      </p>
    </Card>
  );
}

function MiniValue({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-tertiary">{label}</div>
      <div className="mt-0.5 text-sm font-medium text-primary tabular-nums">{value}</div>
    </div>
  );
}

function formatOptionalCurrency(value: number | null): string {
  return value === null ? '—' : formatCurrency(value);
}

function formatScheduleAmountDiscrepancy(
  comparison: ReturnType<typeof computeScheduleAmountComparison>,
): string {
  if (!comparison.canCompare || comparison.discrepancyPaisa === null) return '—';
  if (!comparison.hasDiscrepancy) return 'No discrepancy';
  return formatCurrency(comparison.discrepancyPaisa);
}

// ---------------------------------------------------------------------------
// Section 1 — Project-level totals
// ---------------------------------------------------------------------------

function ProjectTotalsBlock({ project }: { project: Project }) {
  const bothZero = project.contractValuePaisa === 0 && project.costPaisa === 0;
  const marginTone = bothZero
    ? 'neutral'
    : project.marginPaisa > 0
      ? 'success'
      : project.marginPaisa < 0
        ? 'danger'
        : 'neutral';
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
      <StatTile
        label="Contract value"
        value={
          project.contractValuePaisa > 0
            ? formatCurrency(project.contractValuePaisa)
            : '—'
        }
        hint="Sum of qty × client unit price"
      />
      <StatTile
        label="Supplier cost"
        value={project.costPaisa > 0 ? formatCurrency(project.costPaisa) : '—'}
        hint="Sum of part / sub-part supplier costs"
      />
      <StatTile
        label="Margin"
        value={bothZero ? '—' : formatCurrency(project.marginPaisa)}
        tone={marginTone}
        hint={project.marginPaisa < 0 ? 'Cost exceeds contract value' : 'Contract value − cost'}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 2 — Per-part breakdown
// ---------------------------------------------------------------------------

function PartsBreakdownCard({ rows }: { rows: PartFinancialRow[] }) {
  if (rows.length === 0) {
    return (
      <Card title="Parts breakdown">
        <EmptyState
          icon={<Wallet className="h-6 w-6" strokeWidth={1.5} />}
          title="No parts yet"
          body="Once parts are entered, their contract value and cost will appear here."
        />
      </Card>
    );
  }
  return (
    <Card title="Parts breakdown">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border-default text-left text-[11px] font-semibold uppercase tracking-wide text-tertiary">
              <th className="py-2 pr-3">Sr#</th>
              <th className="py-2 pr-3">Part #</th>
              <th className="py-2 pr-3">Noun</th>
              <th className="py-2 pr-3">UOM</th>
              <th className="py-2 pr-3 text-right">Qty</th>
              <th className="py-2 pr-3 text-right">Unit price</th>
              <th className="py-2 pr-3 text-right">Value</th>
              <th className="py-2 pr-3 text-right">GST</th>
              <th className="py-2 pr-3 text-right">GST amount</th>
              <th className="py-2 pr-3 text-right">Total value</th>
              <th className="py-2 pr-3 text-right">Cost</th>
              <th className="py-2 text-right">Margin</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.part.id} className="border-b border-border-default last:border-b-0">
                <td className="py-2 pr-3 font-mono text-xs text-secondary">{row.part.srNumber ?? '—'}</td>
                <td className="py-2 pr-3 text-secondary">{row.part.itemNumber ?? '—'}</td>
                <td className="py-2 pr-3">
                  <div className="text-primary">{row.part.name}</div>
                  {row.isCostRolledUp ? (
                    <div className="text-[11px] text-secondary">Cost rolled up from sub-parts</div>
                  ) : null}
                </td>
                <td className="py-2 pr-3 text-secondary">{row.part.uom ?? '—'}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{row.part.partQuantity ?? 0}</td>
                <td className="py-2 pr-3 text-right tabular-nums">
                  {row.part.clientUnitPricePaisa != null
                    ? formatCurrency(row.part.clientUnitPricePaisa, { prefix: false })
                    : '—'}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums">
                  {formatCurrency(row.contractValuePaisa, { prefix: false })}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums">
                  {formatBpsAsPercent(row.part.gstRateBps)}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums">
                  {row.gstAmountPaisa != null ? formatCurrency(row.gstAmountPaisa, { prefix: false }) : '—'}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums">
                  {row.totalValuePaisa != null ? formatCurrency(row.totalValuePaisa, { prefix: false }) : '—'}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums text-secondary">
                  {formatCurrency(row.costPaisa, { prefix: false })}
                </td>
                <td className="py-2 text-right tabular-nums">
                  <MarginCell value={row.marginPaisa} />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="text-sm font-semibold">
              <td className="py-3" />
              <td className="py-3">Totals</td>
              <td />
              <td />
              <td />
              <td />
              <td className="py-3 pr-3 text-right tabular-nums">
                {formatCurrency(rows.reduce((s, r) => s + r.contractValuePaisa, 0), { prefix: false })}
              </td>
              <td />
              <td className="py-3 pr-3 text-right tabular-nums">
                {formatCurrency(rows.reduce((sum, r) => sum + (r.gstAmountPaisa ?? 0), 0), { prefix: false })}
              </td>
              <td className="py-3 pr-3 text-right tabular-nums">
                {formatCurrency(rows.reduce((sum, r) => sum + (r.totalValuePaisa ?? r.contractValuePaisa), 0), { prefix: false })}
              </td>
              <td className="py-3 pr-3 text-right tabular-nums text-secondary">
                {formatCurrency(rows.reduce((s, r) => s + r.costPaisa, 0), { prefix: false })}
              </td>
              <td className="py-3 text-right tabular-nums">
                <MarginCell value={rows.reduce((s, r) => s + r.marginPaisa, 0)} />
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </Card>
  );
}

function MarginCell({ value }: { value: number }) {
  const Icon = value > 0 ? TrendingUp : value < 0 ? TrendingDown : null;
  const color = value > 0 ? 'text-success-fg' : value < 0 ? 'text-danger-fg' : 'text-secondary';
  return (
    <span className={`inline-flex items-center gap-1 ${color}`}>
      {Icon ? <Icon className="h-3 w-3" strokeWidth={1.75} /> : null}
      {formatCurrency(value, { prefix: false })}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Section 3 — Per-sub-part breakdown
// ---------------------------------------------------------------------------

function SubPartsBreakdownCard({ rows }: { rows: SubPartFinancialRow[] }) {
  return (
    <Card title="Sub-parts breakdown">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border-default text-left text-[11px] font-semibold uppercase tracking-wide text-tertiary">
              <th className="py-2 pr-3">Parent</th>
              <th className="py-2 pr-3">Sr#</th>
              <th className="py-2 pr-3">Sub-part #</th>
              <th className="py-2 pr-3">Noun</th>
              <th className="py-2 pr-3 text-right">Quantity</th>
              <th className="py-2 pr-3 text-right">Supplier price</th>
              <th className="py-2 text-right">Cost</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.subPart.id}
                className="border-b border-border-default last:border-b-0"
              >
                <td className="py-2 pr-3 text-tertiary">
                  <span className="mr-1 font-mono text-xs">{row.parent.srNumber ?? '—'}</span>
                  {row.parent.name}
                </td>
                <td className="py-2 pr-3 font-mono text-xs text-secondary">{row.subPart.srNumber ?? '—'}</td>
                <td className="py-2 pr-3 text-secondary">{row.subPart.itemNumber ?? '—'}</td>
                <td className="py-2 pr-3">{row.subPart.name}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{row.totalRequiredQuantity}</td>
                <td className="py-2 pr-3 text-right tabular-nums">
                  {row.subPart.supplierUnitPricePaisa != null
                    ? formatCurrency(row.subPart.supplierUnitPricePaisa, { prefix: false })
                    : '—'}
                </td>
                <td className="py-2 text-right tabular-nums text-secondary">
                  {formatCurrency(row.costPaisa, { prefix: false })}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="text-sm font-semibold">
              <td className="py-3" />
              <td className="py-3">Total</td>
              <td />
              <td />
              <td />
              <td />
              <td className="py-3 text-right tabular-nums">
                {formatCurrency(rows.reduce((s, r) => s + r.costPaisa, 0), { prefix: false })}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Section 4 — Earnest money
// ---------------------------------------------------------------------------

function EarnestMoneyCard({ project }: { project: Project }) {
  const amount = project.earnestMoneyAmountPaisa;
  if (amount == null) {
    return (
      <Card title="Earnest money">
        <p className="text-sm text-tertiary">Not applicable for this project.</p>
      </Card>
    );
  }
  return (
    <Card title="Earnest money">
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <FinanceField label="Amount" value={formatCurrency(amount)} />
        <FinanceField label="Submitted" value={formatDate(project.earnestSubmittedDate)} />
        <FinanceField
          label="Released"
          value={
            project.earnestReleasedDate
              ? formatDate(project.earnestReleasedDate)
              : 'Pending'
          }
        />
      </dl>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Section 5 — BG amount summary
// ---------------------------------------------------------------------------

function BgAmountSummaryCard({ bgs }: { bgs: BankGuarantee[] }) {
  if (bgs.length === 0) {
    return (
      <Card title="Bank guarantees">
        <p className="text-sm text-tertiary">No BG instruments yet. See the Billing tab to create one.</p>
      </Card>
    );
  }
  const total = bgs.reduce((s, bg) => s + bg.amountPaisa, 0);
  return (
    <Card title="Bank guarantees">
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <FinanceField label="Instruments" value={bgs.length.toString()} />
        <FinanceField label="Total face value" value={formatCurrency(total)} />
      </dl>
      <ul className="mt-3 divide-y divide-border-default">
        {bgs.map((bg) => (
          <li key={bg.id} className="flex items-center gap-2 py-2 text-sm">
            <Banknote className="h-3.5 w-3.5 text-tertiary" strokeWidth={1.5} />
            <span className="flex-1 truncate">{formatCurrency(bg.amountPaisa)}</span>
            <StatusBadge kind="bg" value={bg.status} />
          </li>
        ))}
      </ul>
    </Card>
  );
}

function FinanceField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[11px] font-semibold uppercase tracking-wide text-tertiary">{label}</dt>
      <dd className="mt-0.5 text-sm tabular-nums text-primary">{value}</dd>
    </div>
  );
}
